/**
 * TeamForm Component
 *
 * Reusable form for creating and editing teams
 */

import {
  TextField,
  SelectField,
  TeamAutocompleteField,
  EntityAutocompleteField,
  EntityArrayAutocompleteField,
  type CreateTeamFormData,
} from '../forms';
import { createTeamSchema } from '../forms/validation';
import { ArrayInputField, EntityForm } from '../shared';
import { TeamType } from '../../graphql/types';
import { useAllTeams } from '../../graphql/queries';
import { useUsers } from '../../hooks/useUsers';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './TeamForm.css';

export interface TeamFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateTeamFormData>;
  /** Submit handler */
  onSubmit: (data: CreateTeamFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
  /**
   * The team's own id (edit mode only) — excluded from the Parent Team
   * picker's options so a team can never be set as its own parent (issue
   * #775).
   */
  currentTeamId?: string;
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * Team create/edit form with validation
 *
 * @example
 * ```tsx
 * <TeamForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/teams')}
 * />
 * ```
 */
export function TeamForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  currentTeamId,
}: TeamFormProps): JSX.Element {
  // Teams and users are broadly readable via GraphQL/the existing
  // team-detail member picker (`TeamMembersPanel`), so both catalogs are
  // sourced the same way here rather than gating the form on an admin-only
  // endpoint (issue #775).
  const { data: teamsData } = useAllTeams();
  const teamOptions = (teamsData?.teams?.edges ?? [])
    .map(({ node }) => node)
    .filter((team) => team.entityId !== currentTeamId)
    .map((team) => ({ value: team.entityId, label: team.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const { users } = useUsers();
  const userOptions = [...users]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((user) => ({ value: user.userId, label: `${user.name} (${user.email})` }));

  return (
    <EntityForm<CreateTeamFormData>
      schema={createTeamSchema}
      baseValues={{
        type: 'development',
        memberIds: [],
        responsibilities: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Team"
      formClassName="team-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const memberIds = values.memberIds || [];
        const responsibilities = values.responsibilities || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Team Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Platform Engineering"
                  helperText="A descriptive name for the team"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the team"
                  helperText="Optional description (max 2000 characters)"
                />

                <SelectField
                  label="Team Type"
                  {...register('type')}
                  value={values.type}
                  options={[
                    { value: TeamType.Development, label: 'Development' },
                    { value: TeamType.Operations, label: 'Operations' },
                    { value: TeamType.Platform, label: 'Platform' },
                    { value: TeamType.Security, label: 'Security' },
                    { value: TeamType.Data, label: 'Data' },
                  ]}
                  error={errors.type}
                  required
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Organization</h2>

                <TeamAutocompleteField
                  name="parentTeamId"
                  label="Parent Team"
                  options={teamOptions}
                  value={values.parentTeamId || ''}
                  onChange={(teamId) => setValue('parentTeamId', teamId, { shouldValidate: true })}
                  error={errors.parentTeamId}
                  placeholder="Search teams…"
                  helperText="Optional parent team for hierarchy"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Team Members</h2>

                <EntityAutocompleteField
                  name="leadUserId"
                  label="Team Lead"
                  options={userOptions}
                  value={values.leadUserId || ''}
                  onChange={(userId) => setValue('leadUserId', userId, { shouldValidate: true })}
                  error={errors.leadUserId}
                  required
                  placeholder="Search users…"
                  helperText="The user who leads the team"
                />

                <EntityArrayAutocompleteField
                  name="memberIds"
                  label="Team Members"
                  options={userOptions}
                  value={memberIds}
                  onChange={(ids) => setValue('memberIds', ids)}
                  placeholder="Search users…"
                  removeAriaLabel="Remove member"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Responsibilities</h2>

                <ArrayInputField
                  label="Team Responsibilities"
                  items={responsibilities}
                  onAdd={(value) => setValue('responsibilities', [...responsibilities, value])}
                  onRemove={(index) =>
                    setValue(
                      'responsibilities',
                      responsibilities.filter((_, i) => i !== index)
                    )
                  }
                  placeholder="Enter responsibility and press Enter"
                  removeAriaLabel="Remove responsibility"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Contact Information</h2>

                <TextField
                  label="Contact Email"
                  type="email"
                  {...register('contactEmail')}
                  value={values.contactEmail || ''}
                  error={errors.contactEmail}
                  required
                  placeholder="team@example.com"
                  helperText="Team contact email address"
                />

                <TextField
                  label="Teams Channel"
                  {...register('teamsChannel')}
                  value={values.teamsChannel || ''}
                  error={errors.teamsChannel}
                  placeholder="https://teams.microsoft.com/l/channel/..."
                  helperText="Optional Microsoft Teams channel URL"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
